import '../styles/fonts.css';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import '../styles/homepage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TopStreams from '../components/TopStreams';
import { getTodayRaces, getArticles } from '../services/api';
import UpcomingEvents from '../components/UpcomingEvents';

const SOURCE_COLORS = {
  iracing_news: '#0073e6',
  iracing_blog: '#0073e6',
  srss: '#e65c00',
  internal: '#ff4747',
};

function Home() {
  const { t, i18n } = useTranslation();
  usePageTitle(null);
  useCanonicalTag();
  useMetaTags({
    title: 'Celeritas — iRacing League Manager',
    description: 'Create and manage your iRacing leagues, championships and races. Find and join leagues from the simracing community.',
    url: 'https://celeritas.live/',
  });
  const [todayRaces, setTodayRaces] = useState([]);
  const [articles, setArticles] = useState([]);

  const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';

  function fmtTime(isoStr) {
    if (!isoStr) return '';
    return new Date(isoStr).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }

  useEffect(() => {
    getTodayRaces().then(setTodayRaces).catch(() => {});
    getArticles(1).then(data => setArticles(data.results.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="Home">
      <Header />

      <main>
        <section id="hero-banner">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">{t('home.hero_title')}</h1>
              <p className="hero-sub">{t('home.hero_subtitle')}</p>
            </div>
            <div className="hero-actions">
              <Link to="/add-league" className="hero-btn-primary">
                {t('common.addMyLeague')}
              </Link>
              <Link to="/leagues" className="hero-btn-secondary">
                {t('common.viewLeagues')}
              </Link>
            </div>
          </div>
        </section>

        <section id="today-races" className="races-section">
          <div className="section-header">
            <h2>{t('home.todaysRaces')}</h2>
          </div>

          {todayRaces.length === 0 ? (
            <p style={{ color: '#9ba3ab', fontSize: '13px', padding: '10px 5px' }}>
              {t('home.noRacesToday')}
            </p>
          ) : (
            <ul className="races-of-the-day">
              {todayRaces.map((race, i) => {
                const trackDisplay = [race.track_name, race.config_name].filter(Boolean).join(' — ');
                const inner = (
                  <>
                    <span className="championship-name">{race.league_name}</span>
                    <span className="category-name">{race.championship_name}</span>
                    <span className="track-name">{trackDisplay}</span>
                    <time className="race-time">{fmtTime(race.start_time)}</time>
                  </>
                );
                return (
                  <li key={race.league_session_id ?? i} className="raceitem">
                    <Link to={`/sessions/${race.league_session_id}/live`}>{inner}</Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <div className="content-grid">
          <aside className="left-col">
            <a
              href="https://simracingsemsegredos.com.br/iracing-para-iniciantes/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <section className="course-banner">
                <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8, margin: '0 0 8px' }}>SimRacing Sem Segredos</p>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 8px', lineHeight: 1.3 }}>{t('home.beginnerCourseTitle')}</h3>
                <p style={{ fontSize: '12px', opacity: 0.9, margin: 0, lineHeight: 1.5 }}>{t('home.beginnerCourseDesc')}</p>
                <span className="btn-course">{t('home.beginnerCourseBtn')}</span>
              </section>
            </a>
            <TopStreams />
          </aside>

          <section className="center-col news-feed">
            <div className="box-header">
              <h3>{t('home.latestNews')}</h3>
              <Link to="/news" className="view-all-link" style={{ fontSize: "12px", color: "#ff4747", textDecoration: "none" }}>{t('home.seeAll')}</Link>
            </div>

            {articles.map(article => {
              const color = SOURCE_COLORS[article.source] || '#ff4747';
              const cardInner = (
                <article className="news-card" key={article.id}>
                  <div
                    className="news-thumb"
                    style={{
                      backgroundImage: article.thumbnail_url ? `url('${article.thumbnail_url}')` : 'none',
                      backgroundColor: article.thumbnail_url ? undefined : '#1b2127',
                    }}
                  />
                  <div className="news-info">
                    <span style={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', color, letterSpacing: '0.5px' }}>
                      {article.source_label}
                    </span>
                    <h4 style={{ textDecoration: "none", color: "inherit" }}>{article.title}</h4>
                    <div className="news-meta">
                      <span>{new Date(article.published_at).toLocaleDateString(locale)}</span>
                    </div>
                  </div>
                </article>
              );
              if (article.is_external) {
                return (
                  <a key={article.id} href={article.external_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                    {cardInner}
                  </a>
                );
              }
              return (
                <Link key={article.id} to={`/news/${article.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  {cardInner}
                </Link>
              );
            })}

            {articles.length === 0 && (
              <p style={{ color: '#9ba3ab', fontSize: '13px' }}>{t('home.loadingNews')}</p>
            )}
          </section>

          <aside className="right-col">
            <a
              href="https://simracingsemsegredos.com.br/coaching/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <section className="course-banner">
                <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8, margin: '0 0 8px' }}>SimRacing Sem Segredos</p>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 8px', lineHeight: 1.3 }}>{t('home.coachingTitle')}</h3>
                <p style={{ fontSize: '12px', opacity: 0.9, margin: 0, lineHeight: 1.5 }}>{t('home.coachingDesc')}</p>
                <span className="btn-course" style={{ color: '#1a6fa8' }}>{t('home.coachingBtn')}</span>
              </section>
            </a>

            <UpcomingEvents />
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Home;
